import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, viewChild } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { AccessLevel } from "@front/m19-api-client";
import {
  AccountSelectionService,
  AuthService,
  isReadOnlyAccess,
  isSuspendedForAdmin,
  OrganizationAccountGroupService,
  UserSelectionService,
} from "@front/m19-services";
import { IAlertComponent, IButtonDirective, IIconComponent, ITooltipDirective, SpinnerComponent } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AccountSelectorComponent } from "@m19-board/account-selector/account-selector.component";
import { MultiAccountSelectorComponent } from "@m19-board/account-selector/multi-account-selector.component";
import { PageLayoutService } from "@m19-board/containers/page-layout/page-layout.service";
import { CurrencySelectionComponent } from "@m19-board/currency-selector/currency-selection.component";
import { DateRangeSelectorComponent } from "@m19-board/date-range-selector/date-range-selector.component";
import { LayoutSelectorService } from "@m19-board/layout-selector.service";
import { OrganizationSelectorComponent } from "@m19-board/organization-selector/organization-selector.component";
import { AccountSettingsV2Service } from "@m19-board/services/account-settings.service";
import { LoadingService } from "@m19-board/services/loading.service";
import { BreadcrumbItemComponent } from "@m19-board/shared/breadcrumb-item/breadcrumb-item.component";
import { M19RouteData } from "@m19-board/utils/M19Route";
import { combineLatest, map, Observable, switchMap } from "rxjs";
import { OverlayService } from "@m19-board/overlay/overlay-service/overlay.service";

@Component({
  selector: "app-page-layout",
  imports: [
    CommonModule,
    DateRangeSelectorComponent,
    CurrencySelectionComponent,
    MultiAccountSelectorComponent,
    OrganizationSelectorComponent,
    IButtonDirective,
    ITooltipDirective,
    RouterLink,
    TranslocoDirective,
    AccountSelectorComponent,
    BreadcrumbItemComponent,
    IAlertComponent,
    IIconComponent,
    SpinnerComponent,
  ],
  templateUrl: "./page-layout.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageLayoutComponent {
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly translocoService = inject(TranslocoService);
  private readonly titleService = inject(Title);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly layoutSelectorService = inject(LayoutSelectorService);
  private readonly loadingService = inject(LoadingService);
  private readonly pageLayoutService = inject(PageLayoutService);
  private readonly accountSettingsService = inject(AccountSettingsV2Service);
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly authService = inject(AuthService);
  private readonly overlayService = inject(OverlayService);

  readonly AccessLevel = AccessLevel;

  private routeData$: Observable<M19RouteData> = this.pageLayoutService.navigationEvents$.pipe(
    map(() => this.activatedRoute),
    switchMap((route) => {
      const data = [route.data];
      while (route.firstChild) {
        route = route.firstChild;
        data.push(route.data);
      }
      return combineLatest(data).pipe(map((allData) => allData.reduce((acc, data) => ({ ...acc, ...data }), {})));
    }),
  );
  private routeData = toSignal<M19RouteData>(this.routeData$);
  parentTitle = computed<string>(() => this.translocoService.translate(this.routeData()?.parentTitle ?? ""));
  parentRouteUrl = computed(() => this.routeData()?.parentRouteUrl);
  title = computed(() => this.routeData()?.title);
  isPadded = computed(() => this.routeData()?.padded !== false);

  private readonly overlay$ = this.routeData$.pipe(
    switchMap((routeData) => this.overlayService.resolveOverlay(routeData?.overlays ?? [])),
  );
  overlay = toSignal(this.overlay$);

  private title$ = toObservable(this.parentTitle);

  withDatePicker = computed(() => this.routeData()?.withDatePicker ?? false);
  withCurrencySelector = computed(() => this.routeData()?.withCurrencySelector ?? false);
  withAccountSelector = computed(() => this.routeData()?.withAccountSelector ?? false);
  withMultiAccountSelector = computed(() => this.routeData()?.withMultiAccountSelector ?? false);
  withOrganizationSelector = computed(() => this.routeData()?.withOrganizationSelector ?? false);
  withChatbotSafeMargin = computed(() => this.routeData()?.withChatbotSafeMargin ?? false);
  subPage = computed(() => this.routeData()?.subPage ?? false);

  isSidebarCollapsed = this.userSelectionService.sidebarCollapsed;
  loading = this.loadingService.loading;
  readonly locale = toSignal(this.authService.userLocale$);

  private readonly selectedAccountGroup = toSignal(this.accountSelectionService.accountGroupSelection$);
  private readonly allAccountMarketplaces = toSignal(
    this.organizationAccountGroupService.allOrganizationAccountGroups$.pipe(
      map((orgs) => orgs.flatMap((org) => org.accountGroups.flatMap((group) => group.getAccountMarketplaces()))),
    ),
    { initialValue: [] },
  );

  invalidAccountMarketplaces = computed(() => {
    const accountMarketplaces =
      this.withAccountSelector() || this.withMultiAccountSelector()
        ? (this.selectedAccountGroup()?.getAccountMarketplaces() ?? [])
        : this.allAccountMarketplaces();
    return accountMarketplaces.filter((am) => !isReadOnlyAccess(am.accessLevel) && !am.isValidToken);
  });

  readonly suspendedOrganization = toSignal(
    this.organizationAccountGroupService.allOrganizationAccountGroups$.pipe(
      map((orgs) => orgs.find(isSuspendedForAdmin) ?? orgs.find((org) => org.organization.suspensionDate)),
    ),
  );
  readonly isSuspensionEffective = computed(() => {
    const org = this.suspendedOrganization();
    return !!org && isSuspendedForAdmin(org);
  });

  private readonly breadcrumbPortalOutlet = viewChild<ElementRef<HTMLElement>>("breadcrumbPortalOutlet");

  constructor() {
    this.title$
      .pipe(takeUntilDestroyed())
      .subscribe((title) =>
        this.titleService.setTitle(this.layoutSelectorService.getAgencyName() + (title ? " - " + title : "")),
      );

    this.routeData$.pipe(takeUntilDestroyed()).subscribe((routeData: M19RouteData) => {
      this.accountSelectionService.setSingleAccountMarketplaceMode(!routeData.withMultiAccountSelector);
    });

    effect(() => {
      const outlet = this.breadcrumbPortalOutlet();
      if (outlet) this.pageLayoutService.registerPortalOutlet(outlet.nativeElement);
    });
  }

  requestGrant() {
    this.accountSettingsService.openGrantSellingPartnerAccessModal(this.invalidAccountMarketplaces());
  }
}
